<script setup lang="ts">
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useLintNotesStore } from '../stores/lint_notes'
import { useSynthesisSuggestionsStore } from '../stores/synthesis_suggestions'
import { REVIEW_QUEUES } from '../lib/navConfig'

// The Review hub's tab strip: one link per queue with its open count, so
// /lint and /synthesis read as two views of one inbox. The nav rail's
// Review entry lands on the first queue and stays lit on either.
const route = useRoute()
const { openCount: lintOpen } = storeToRefs(useLintNotesStore())
const { openCount: synthesisOpen } = storeToRefs(useSynthesisSuggestionsStore())

const counts = computed<Record<string, number>>(() => ({ lint: lintOpen.value, synthesis: synthesisOpen.value }))

const tabs = computed(() =>
  REVIEW_QUEUES.map((queue) => ({
    key: queue.key,
    label: queue.label,
    to: queue.to,
    count: counts.value[queue.key] ?? 0,
    active: route.path === queue.to,
  })),
)
</script>

<template>
  <nav aria-label="Review queues" class="flex flex-wrap gap-2" data-testid="review-tabs">
    <RouterLink
      v-for="tab in tabs"
      :key="tab.key"
      :to="tab.to"
      :data-testid="`review-tab-${tab.key}`"
      :aria-current="tab.active ? 'page' : undefined"
      class="inline-flex min-h-9 items-center gap-2 rounded-full border px-3.5 text-[13px] no-underline transition-colors"
      :class="
        tab.active ? 'border-attention bg-attention/10 text-ink' : 'border-border-control text-ink-muted hover:text-ink'
      "
    >
      {{ tab.label }}
      <span class="font-semibold" :class="tab.active ? 'text-attention' : 'text-ink-faint'">{{ tab.count }}</span>
    </RouterLink>
  </nav>
</template>
